import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { STARTUP_API_URL } from "../apiConfig";
import { validateFields } from "../lib/guardrails";
import { getPermissions } from "../lib/permissions";
import Navbar from "../components/navbar";
import Footer from "../components/Footer";
import {
  FiPlus, FiEdit2, FiTrash2, FiCheck, FiX, FiUpload,
  FiSearch, FiChevronLeft, FiChevronRight, FiRefreshCw,
  FiAlertCircle, FiEye
} from "react-icons/fi";

interface Startup {
  id: number;
  name: string;
  description?: string;
  short_description: string;
  website: string;
  founded_year?: number | null;
  employee_count?: string | null;
  headquarters?: string | null;
  funding_stage: string;
  total_funding: number;
  email?: string | null;
  phone?: string | null;
  linkedin_url?: string | null;
  twitter_url?: string | null;
  crunchbase_url?: string | null;
  is_approved: boolean;
  is_verified: boolean;
  data_source: string;
  created_at: string;
  category?: { id: number; name: string };
  region?: { country: string; state: string; city: string };
}

interface Stats {
  total_startups: number;
  approved_startups: number;
  pending_startups: number;
  total_engagements: number;
  total_chat_sessions: number;
}

const AdminPanel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const perms = getPermissions(localStorage.getItem("userRole"));
  const [startups, setStartups] = useState<Startup[]>([]);
  const [stats, setStats] = useState<Stats>({
    total_startups: 0,
    approved_startups: 0,
    pending_startups: 0,
    total_engagements: 0,
    total_chat_sessions: 0,
  });
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: number | null; all: boolean }>({ open: false, id: null, all: false });
  const [editingStartup, setEditingStartup] = useState<Startup | null>(null);
  const [viewMode, setViewMode] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state for add/edit
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    short_description: "",
    website: "",
    founded_year: "",
    employee_count: "",
    headquarters: "",
    funding_stage: "unknown",
    total_funding: "",
    email: "",
    phone: "",
    linkedin_url: "",
    twitter_url: "",
    crunchbase_url: "",
  });

  useEffect(() => {
    const q = new URLSearchParams(location.search).get("search");
    if (q) setSearchQuery(q);
  }, [location.search]);

  useEffect(() => {
    const prefill = (location.state as any)?.prefill;
    if (prefill) {
      setFormData({
        name: prefill.name || "",
        description: prefill.description || "",
        short_description: prefill.short_description || "",
        website: prefill.website || "",
        founded_year: prefill.founded_year || "",
        employee_count: prefill.employee_count || "",
        headquarters: prefill.headquarters || "",
        funding_stage: prefill.funding_stage || "unknown",
        total_funding: prefill.total_funding || "",
        email: prefill.email || "",
        phone: prefill.phone || "",
        linkedin_url: prefill.linkedin_url || "",
        twitter_url: prefill.twitter_url || "",
        crunchbase_url: prefill.crunchbase_url || "",
      });
      setEditingStartup(null);
      setShowAddModal(true);
      // Clear state so re-renders don't re-open the modal
      window.history.replaceState({}, document.title);
    }
  }, []);

  useEffect(() => {
    fetchStartups();
    fetchStats();
  }, [currentPage, searchQuery]);

  const fetchStartups = async () => {
    try {
      setLoading(true);
      setError(null);
      const params: any = {
        page: currentPage,
        page_size: 10,
      };
      if (searchQuery) params.search = searchQuery;

      const response = await axios.get(`${STARTUP_API_URL}/api/v1/admin/startups`, { params });
      setStartups(response.data.data || []);
      setTotalPages(response.data.pagination?.total_pages || 1);
    } catch (error: any) {
      console.error("Error fetching startups:", error);
      if (error.code === "ERR_NETWORK") {
        setError("Unable to connect to server. Please ensure the backend is running.");
      } else {
        setError("Failed to load startups. Please try again.");
      }
      setStartups([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const response = await axios.get(`${STARTUP_API_URL}/api/v1/admin/stats/overview`);
      setStats(response.data.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
      // Keep default stats values on error
    } finally {
      setStatsLoading(false);
    }
  };

  const handleDelete = (id: number) => {
    setDeleteDialog({ open: true, id, all: false });
  };

  const handleDeleteAll = () => {
    setDeleteDialog({ open: true, id: null, all: true });
  };

  const confirmDelete = async () => {
    const { id, all } = deleteDialog;
    setDeleteDialog({ open: false, id: null, all: false });
    try {
      if (all) {
        await axios.delete(`${STARTUP_API_URL}/api/v1/admin/startups/bulk-delete`, { data: { ids: null } });
        fetchStartups();
        fetchStats();
        setSelectedIds([]);
      } else if (id !== null) {
        await axios.delete(`${STARTUP_API_URL}/api/v1/admin/startups/${id}`);
        fetchStartups();
        fetchStats();
      }
    } catch (error) {
      console.error("Error deleting startup:", error);
    }
  };

  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setFormError("Startup name is required.");
      setTimeout(() => setFormError(null), 4000);
      return;
    }

    const fieldsToCheck = [
      { value: formData.name, name: "Name" },
      ...(formData.short_description ? [{ value: formData.short_description, name: "Short Description" }] : []),
      ...(formData.description ? [{ value: formData.description, name: "Description" }] : []),
      ...(formData.headquarters ? [{ value: formData.headquarters, name: "Headquarters" }] : []),
    ];
    const contentCheck = validateFields(fieldsToCheck);
    if (!contentCheck.valid) {
      setFormError(contentCheck.message);
      setTimeout(() => setFormError(null), 4000);
      return;
    }

    setFormError(null);
    try {
      const payload = {
        ...formData,
        founded_year: formData.founded_year ? parseInt(formData.founded_year) : null,
        total_funding: formData.total_funding ? parseFloat(formData.total_funding) : null,
      };

      if (editingStartup) {
        await axios.put(`${STARTUP_API_URL}/api/v1/admin/startups/${editingStartup.id}`, payload);
      } else {
        await axios.post(`${STARTUP_API_URL}/api/v1/admin/startups`, payload);
      }

      const message = editingStartup ? "Startup updated successfully!" : "Startup created successfully!";
      setShowAddModal(false);
      setEditingStartup(null);
      resetForm();
      fetchStartups();
      fetchStats();
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || "Failed to save startup. Please try again.";
      setFormError(msg);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      short_description: "",
      website: "",
      founded_year: "",
      employee_count: "",
      headquarters: "",
      funding_stage: "unknown",
      total_funding: "",
      email: "",
      phone: "",
      linkedin_url: "",
      twitter_url: "",
      crunchbase_url: "",
    });
  };

  const openViewModal = (startup: Startup) => {
    setViewMode(true);
    setEditingStartup(startup);
    setFormData({
      name: startup.name || "",
      description: startup.description || "",
      short_description: startup.short_description || "",
      website: startup.website || "",
      founded_year: startup.founded_year?.toString() || "",
      employee_count: startup.employee_count || "",
      headquarters: startup.headquarters
        || [startup.region?.city, startup.region?.state, startup.region?.country].filter(Boolean).join(", ")
        || "",
      funding_stage: startup.funding_stage || "unknown",
      total_funding: startup.total_funding?.toString() || "",
      email: startup.email || "",
      phone: startup.phone || "",
      linkedin_url: startup.linkedin_url || "",
      twitter_url: startup.twitter_url || "",
      crunchbase_url: startup.crunchbase_url || "",
    });
    setShowAddModal(true);
  };

  const openEditModal = (startup: Startup) => {
    setViewMode(false);
    setEditingStartup(startup);
    setFormData({
      name: startup.name || "",
      description: startup.description || "",
      short_description: startup.short_description || "",
      website: startup.website || "",
      founded_year: startup.founded_year?.toString() || "",
      employee_count: startup.employee_count || "",
      headquarters: startup.headquarters
        || [startup.region?.city, startup.region?.state, startup.region?.country].filter(Boolean).join(", ")
        || "",
      funding_stage: startup.funding_stage || "unknown",
      total_funding: startup.total_funding?.toString() || "",
      email: startup.email || "",
      phone: startup.phone || "",
      linkedin_url: startup.linkedin_url || "",
      twitter_url: startup.twitter_url || "",
      crunchbase_url: startup.crunchbase_url || "",
    });
    setShowAddModal(true);
  };

  const formatFunding = (amount: number) => {
    if (!amount) return "-";
    if (amount >= 1e9) return `$${(amount / 1e9).toFixed(1)}B`;
    if (amount >= 1e6) return `$${(amount / 1e6).toFixed(1)}M`;
    if (amount >= 1e3) return `$${(amount / 1e3).toFixed(0)}K`;
    return `$${amount}`;
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        background: "radial-gradient(35% 19% at 50% 0%, rgba(255, 84, 31, 0.5) 0%, rgba(0, 0, 0, 0) 100%), black",
        color: "white",
      }}
    >
      {/* Delete Confirmation Dialog */}
      {deleteDialog.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-[#111] border border-white/10 rounded-2xl p-6 w-full max-w-sm mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/15 flex items-center justify-center flex-shrink-0">
                <FiTrash2 className="text-red-400 w-5 h-5" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm">Delete Permanently?</p>
                <p className="text-gray-400 text-xs mt-0.5">
                  {deleteDialog.all
                    ? `This will permanently delete all ${startups.length} startups. This action cannot be undone.`
                    : "Are you sure you want to delete this startup? This action cannot be undone."}
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setDeleteDialog({ open: false, id: null, all: false })}
                className="px-4 py-2 rounded-lg text-sm text-gray-300 bg-white/5 hover:bg-white/10 border border-white/10 transition"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 rounded-lg text-sm text-white bg-red-600 hover:bg-red-700 transition flex items-center gap-1.5"
              >
                <FiTrash2 className="w-3.5 h-3.5" /> Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navbar */}
      <div className="w-full bg-transparent border-b border-white/10">
        <Navbar onSearchClick={() => {}} />
      </div>

      {/* Main Content */}
      <div className="flex-1 w-full max-w-7xl mx-auto px-4 pt-24 pb-8">
        {/* Success Toast */}
        {successMessage && (
          <div className="fixed top-24 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 rounded-xl bg-green-500/20 border border-green-500/30 text-green-400 shadow-lg">
            <FiCheck className="w-5 h-5 flex-shrink-0" />
            <span className="font-medium">{successMessage}</span>
          </div>
        )}

        {/* Connection Status Banner */}
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center gap-3">
            <FiAlertCircle className="text-red-400 flex-shrink-0" />
            <p className="text-red-400 text-sm flex-1">{error}</p>
            <button
              onClick={() => { fetchStartups(); fetchStats(); }}
              className="flex items-center gap-2 px-3 py-1 rounded-lg bg-red-500/20 hover:bg-red-500/30 transition text-red-400 text-sm"
            >
              <FiRefreshCw className="w-4 h-4" /> Retry
            </button>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white px-3 py-1.5 mb-3 rounded-lg hover:bg-white/10 transition-all border border-white/10"
            >
              ← Back to Chat
            </button>
            <h1 className="text-3xl font-bold" style={{ fontFamily: "Sora" }}>
              Control Center
            </h1>
            <p className="text-gray-400 mt-1">Manage startups, categories, and data</p>
          </div>
          <div className="flex gap-3 mt-4 md:mt-0">
            <button
              onClick={() => { fetchStartups(); fetchStats(); }}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-white/20 hover:bg-white/10 transition disabled:opacity-50"
            >
              <FiRefreshCw className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              onClick={() => navigate("/admin/excel-upload")}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-white/20 hover:bg-white/10 transition"
            >
              <FiUpload /> Upload Excel
            </button>
            <button
              onClick={() => {
                resetForm();
                setEditingStartup(null);
                setViewMode(false);
                setShowAddModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FF541F] hover:bg-[#e54a1a] transition"
            >
              <FiPlus /> Add Startup
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          {[
            { label: "Total Startups", value: stats.total_startups, color: "#3B82F6" },
            { label: "Engagements", value: stats.total_engagements, color: "#22C55E" },
            { label: "Chat Sessions (All users)", value: stats.total_chat_sessions, color: "#F59E0B" },
          ].map((stat) => (
            <div
              key={stat.label}
              className="p-4 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors"
            >
              <p className="text-gray-400 text-sm">{stat.label}</p>
              {statsLoading ? (
                <div className="h-8 mt-1 bg-white/10 rounded animate-pulse w-16" />
              ) : (
                <p className="text-2xl font-bold mt-1" style={{ color: stat.color }}>
                  {stat.value.toLocaleString()}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search startups..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-[#FF541F]"
            />
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedIds.length > 0 && (
          <div className="flex items-center gap-4 mb-4 p-4 rounded-lg bg-white/5 border border-white/10">
            <span className="text-gray-400">{selectedIds.length} selected</span>
            <button
              onClick={() => setSelectedIds([])}
              className="ml-auto text-gray-400 hover:text-white"
            >
              Clear Selection
            </button>
          </div>
        )}

        {/* Delete All */}
        {perms.deleteStartups && startups.length > 0 && (
          <div className="flex justify-end mb-3">
            <button
              onClick={handleDeleteAll}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-700 hover:bg-red-800 transition text-sm text-white"
            >
              <FiTrash2 /> Delete All ({startups.length})
            </button>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full">
            <thead className="bg-white/5">
              <tr>
                <th className="p-4 text-left">
                  <input
                    type="checkbox"
                    checked={selectedIds.length === startups.length && startups.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedIds(startups.map((s) => s.id));
                      } else {
                        setSelectedIds([]);
                      }
                    }}
                    className="w-4 h-4 rounded"
                  />
                </th>
                <th className="p-4 text-left text-gray-400 font-medium">Name</th>
                <th className="p-4 text-left text-gray-400 font-medium">Category</th>
                <th className="p-4 text-left text-gray-400 font-medium">Funding</th>
                <th className="p-4 text-left text-gray-400 font-medium">Source</th>
                <th className="p-4 text-left text-gray-400 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                // Loading skeleton rows
                [...Array(5)].map((_, idx) => (
                  <tr key={idx} className="border-t border-white/5">
                    <td className="p-4">
                      <div className="w-4 h-4 bg-white/10 rounded animate-pulse" />
                    </td>
                    <td className="p-4">
                      <div className="space-y-2">
                        <div className="h-4 bg-white/10 rounded animate-pulse w-32" />
                        <div className="h-3 bg-white/5 rounded animate-pulse w-48" />
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="h-4 bg-white/10 rounded animate-pulse w-20" />
                    </td>
                    <td className="p-4">
                      <div className="h-4 bg-white/10 rounded animate-pulse w-16" />
                    </td>
                    <td className="p-4">
                      <div className="h-5 bg-white/10 rounded-full animate-pulse w-14" />
                    </td>
                    <td className="p-4">
                      <div className="h-5 bg-white/10 rounded-full animate-pulse w-16" />
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <div className="w-8 h-8 bg-white/10 rounded animate-pulse" />
                        <div className="w-8 h-8 bg-white/10 rounded animate-pulse" />
                      </div>
                    </td>
                  </tr>
                ))
              ) : error ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center">
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center">
                        <FiX className="w-8 h-8 text-red-400" />
                      </div>
                      <div>
                        <p className="text-red-400 font-medium">{error}</p>
                        <p className="text-gray-500 text-sm mt-1">
                          Run: uvicorn main:app --host 0.0.0.0 --port 8000 --reload
                        </p>
                      </div>
                      <button
                        onClick={() => { fetchStartups(); fetchStats(); }}
                        className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition flex items-center gap-2"
                      >
                        <FiSearch /> Retry
                      </button>
                    </div>
                  </td>
                </tr>
              ) : startups.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center">
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center">
                        <FiSearch className="w-8 h-8 text-gray-500" />
                      </div>
                      <div>
                        <p className="text-gray-400 font-medium">No startups found</p>
                        <p className="text-gray-500 text-sm mt-1">
                          {searchQuery ? "Try a different search term" : "Add your first startup to get started"}
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                startups.map((startup) => (
                  <tr key={startup.id} className="border-t border-white/5 hover:bg-white/5">
                    <td className="p-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(startup.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedIds([...selectedIds, startup.id]);
                          } else {
                            setSelectedIds(selectedIds.filter((id) => id !== startup.id));
                          }
                        }}
                        className="w-4 h-4 rounded"
                      />
                    </td>
                    <td className="p-4">
                      <div>
                        <p className="font-medium">{startup.name}</p>
                        <p className="text-sm text-gray-400 truncate max-w-xs">
                          {startup.short_description}
                        </p>
                      </div>
                    </td>
                    <td className="p-4 text-gray-300">
                      {startup.category?.name || "-"}
                    </td>
                    <td className="p-4">
                      <span className="text-green-400">
                        {formatFunding(startup.total_funding)}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="px-2 py-1 text-xs rounded-full bg-white/10">
                        {startup.data_source}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openViewModal(startup)}
                          title="View details"
                          className="p-2 rounded-lg hover:bg-white/10 transition"
                        >
                          <FiEye className="text-gray-400 hover:text-blue-400" />
                        </button>
                        <button
                          onClick={() => openEditModal(startup)}
                          className="p-2 rounded-lg hover:bg-white/10 transition"
                        >
                          <FiEdit2 className="text-gray-400 hover:text-white" />
                        </button>
                        {perms.deleteStartups && (
                          <button
                            onClick={() => handleDelete(startup.id)}
                            className="p-2 rounded-lg hover:bg-white/10 transition"
                          >
                            <FiTrash2 className="text-gray-400 hover:text-red-400" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-6">
          <p className="text-gray-400">
            Page {currentPage} of {totalPages}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-lg border border-white/10 hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiChevronLeft />
            </button>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg border border-white/10 hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiChevronRight />
            </button>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a1a] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-white/10">
            <div className="p-6 border-b border-white/10">
              <h2 className="text-xl font-bold">
                {viewMode ? "View Startup" : editingStartup ? "Edit Startup" : "Add New Startup"}
              </h2>
            </div>
            <form onSubmit={handleSubmitForm} className={`p-6 space-y-4 ${viewMode ? "pointer-events-none select-none opacity-80" : ""}`}>
              {formError && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                  <FiAlertCircle className="flex-shrink-0" />
                  {formError}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#FF541F]"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Website</label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#FF541F]"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-gray-400 text-sm mb-1">Short Description</label>
                  <input
                    type="text"
                    value={formData.short_description}
                    onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#FF541F]"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-gray-400 text-sm mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#FF541F]"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Founded Year</label>
                  <input
                    type="number"
                    value={formData.founded_year}
                    onChange={(e) => setFormData({ ...formData, founded_year: e.target.value })}
                    min="1900"
                    max="2030"
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#FF541F]"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Employee Count</label>
                  <select
                    value={formData.employee_count}
                    onChange={(e) => setFormData({ ...formData, employee_count: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#FF541F]"
                  >
                    <option value="">Select</option>
                    <option value="1-10">1-10</option>
                    <option value="11-50">11-50</option>
                    <option value="51-200">51-200</option>
                    <option value="201-500">201-500</option>
                    <option value="501-1000">501-1000</option>
                    <option value="1000+">1000+</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Funding Stage</label>
                  <select
                    value={formData.funding_stage}
                    onChange={(e) => setFormData({ ...formData, funding_stage: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#FF541F]"
                  >
                    <option value="unknown">Unknown</option>
                    <option value="pre_seed">Pre-Seed</option>
                    <option value="seed">Seed</option>
                    <option value="series_a">Series A</option>
                    <option value="series_b">Series B</option>
                    <option value="series_c">Series C</option>
                    <option value="series_d_plus">Series D+</option>
                    <option value="ipo">IPO</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Total Funding (USD)</label>
                  <input
                    type="number"
                    value={formData.total_funding}
                    onChange={(e) => setFormData({ ...formData, total_funding: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#FF541F]"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Headquarters</label>
                  <input
                    type="text"
                    value={formData.headquarters}
                    onChange={(e) => setFormData({ ...formData, headquarters: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#FF541F]"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#FF541F]"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">LinkedIn URL</label>
                  <input
                    type="url"
                    value={formData.linkedin_url}
                    onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#FF541F]"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Phone</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#FF541F]"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Twitter URL</label>
                  <input
                    type="url"
                    value={formData.twitter_url}
                    onChange={(e) => setFormData({ ...formData, twitter_url: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#FF541F]"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Crunchbase URL</label>
                  <input
                    type="url"
                    value={formData.crunchbase_url}
                    onChange={(e) => setFormData({ ...formData, crunchbase_url: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#FF541F]"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 pointer-events-auto">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingStartup(null);
                    setViewMode(false);
                  }}
                  className="px-6 py-2 rounded-lg border border-white/20 hover:bg-white/10 transition"
                >
                  Close
                </button>
                {!viewMode && (
                  <button
                    type="submit"
                    className="px-6 py-2 rounded-lg bg-[#FF541F] hover:bg-[#e54a1a] transition"
                  >
                    {editingStartup ? "Update" : "Create"}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AdminPanel;
